import { api } from "../../config";
import lightLogo from "../../assets/images/light-logo.png";
import darkLogo from "../../assets/images/logo-dark.png";

// Modern and Professional Invoice Template
export const getInvoiceHTML = (booking, settings = null) => {
    // Helper to format date like "31 Dec 2025"
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) { return dateString; }
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        try {
            // handle if timeString already includes AM/PM
            if (timeString.toLowerCase().includes('m')) return timeString;

            // Handle ISO 8601 strings (like from Backend)
            if (timeString.includes('T') || timeString.includes('-')) {
                const date = new Date(timeString);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    });
                }
            }

            const parts = timeString.split(':');
            if (parts.length < 2) return timeString;

            const hours = parseInt(parts[0], 10);
            const minutes = parts[1].substring(0, 2);
            if (isNaN(hours)) return timeString;

            const ampm = hours >= 12 ? 'PM' : 'AM';
            const h12 = hours % 12 || 12;
            return `${h12}:${minutes} ${ampm}`;
        } catch (e) { return timeString; }
    };

    const formatCurrency = (amount) => {
        return '₹' + (amount || 0).toLocaleString('en-IN');
    };

    // Logic to get the correct logo URL
    const getLogoUrl = () => {
        // High priority: use the local logo requested
        if (lightLogo) return lightLogo;

        const logoData = settings?.logo;

        let path = "";
        if (typeof logoData === 'string') {
            path = logoData;
        } else {
            path = logoData.url || logoData.imagePath || logoData.path || logoData.logo || logoData.logoUrl || "";
            if (!path && logoData.data) {
                const d = logoData.data;
                path = d.url || d.imagePath || d.path || d.logo || d.logoUrl || (typeof d === 'string' ? d : "");
            }
        }

        if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) return path;

        // Clean path
        const cleanPath = path.replace(/^[/\\]+/, '').replace(/\\/g, '/');

        // Development proxy handling


        // Construct API URL
        let apiUrl = api?.IMG_URL || api?.API_URL || process.env.REACT_APP_IMG_URL || "https://api.farmstayhub.com";

        const baseUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
        return `${baseUrl}${cleanPath}`;
    };

    const logoUrl = getLogoUrl();

    // Status mapping
    const status = (booking.paymentStatus || booking.status || 'PENDING').toUpperCase();
    let statusColor = '#ca8a04'; // Pending
    let statusBg = '#fff9e6';

    if (status === 'PAID') {
        statusColor = '#16a34a';
        statusBg = '#e6f7ed';
    } else if (status === 'PARTIAL') {
        statusColor = '#2563eb';
        statusBg = '#e6f0ff';
    } else if (status === 'CANCEL' || status === 'CANCELLED') {
        statusColor = '#dc2626';
        statusBg = '#fef2f2';
    }

    const invoiceNumber = booking.invoiceToken || `INV-${booking.id}`;
    const invoiceDate = formatDate(booking.updatedAt || new Date());

    const checkIn = formatDate(booking.startDate || booking.bookingDate);
    const checkInTime = formatTime(booking.checkInTime || booking.bookingTimeFrom || "10:00");

    const checkOut = formatDate(booking.endDate || booking.bookingEndDate || booking.startDate);
    const checkOutTime = formatTime(booking.checkOutTime || booking.bookingTimeTo || "10:00");

    // Dynamic duration calculation for fallback
    const calculateDurationHours = () => {
        // If bookingHours exists and isn't 12 (default) return it, 
        // unless it's a multi-day booking where 12 might be the default error
        const savedHours = parseInt(booking.bookingHours || 0);

        try {
            const startStr = booking.startDate || booking.bookingDate;
            const endStr = booking.endDate || booking.bookingEndDate || booking.startDate;
            const startTimeStr = booking.checkInTime || booking.bookingTimeFrom || "07:00";
            const endTimeStr = booking.checkOutTime || booking.bookingTimeTo || "18:00";

            const start = new Date(`${startStr}T${startTimeStr.includes(':') ? startTimeStr.substring(0, 5) : '07:00'}`);
            const end = new Date(`${endStr}T${endTimeStr.includes(':') ? endTimeStr.substring(0, 5) : '18:00'}`);

            if (isNaN(start.getTime()) || isNaN(end.getTime())) return savedHours || 12;

            const diffMs = end - start;
            const actualHours = Math.round(diffMs / (1000 * 60 * 60));

            if (actualHours <= 0) return savedHours || 12;

            // If savedHours matches or is reasonably close to actual, use it
            // Otherwise, if savedHours is 12 but it's clearly a longer stay, use actual rounded to 12h
            const roundedActual = Math.ceil(actualHours / 12) * 12;

            if (savedHours > 0 && savedHours !== 12) return savedHours;
            return roundedActual;
        } catch (e) {
            return savedHours || 12;
        }
    };

    const durationHours = calculateDurationHours();

    const guests = booking.numberOfPersons || 1;
    const rawType = booking.bookingType || 'REGULAR_12HR';
    const bookingType = rawType.includes('WEEKEND') ? 'WEEKEND' : 'REGULAR';

    // Calculations for summary as per requirements
    const basicRate = booking.originalPrice || 0;
    const extraPersonCount = booking.extraPersonInfo?.extraPersonCount || 0;
    const priceOption = booking.farmhouse?.priceOptions?.find(o => o.category === booking.bookingType);
    const extraPersonRate = booking.extraPersonInfo?.extraPersonPricePerPerson ?? (priceOption?.extraPersonPrice ?? booking.farmhouse?.extraPersonPrice) ?? 0;
    const extraPersonTotal = (booking.extraPersonInfo?.extraPersonTotalPrice || booking.extraPersonCharge || (extraPersonCount * extraPersonRate));
    const discount = booking.discountAmount || 0;
    const finalTotal = booking.finalTotal || booking.finalPrice || 0;
    const isPartialPayment = String(booking.paymentStatus || '').toUpperCase() === 'PARTIAL';
    const normalizedStatus = String(booking.paymentStatus || booking.status || '').toUpperCase();
    const useCompactLayout =
        isPartialPayment || normalizedStatus === 'INCOMPLETE' || normalizedStatus === 'PENDING';
    const normalizedPaidAmount =
        (normalizedStatus === 'PAID')
            ? finalTotal
            : ((normalizedStatus === 'INCOMPLETE' || normalizedStatus === 'PENDING')
                ? 0
                : (booking.paidAmount || 0));
    const normalizedRemainingAmount =
        (normalizedStatus === 'PAID')
            ? 0
            : ((normalizedStatus === 'INCOMPLETE' || normalizedStatus === 'PENDING')
                ? finalTotal
                : (booking.remainingAmount || 0));

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Invoice - ${invoiceNumber}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
    <style>
        :root {
            --primary: #1f2f26ff;
            --primary-light: rgba(0, 0, 0, 0.02);
            --secondary: #64748b;
            --text-dark: #1e293b;
            --text-light: #64748b;
            --border: #e2e8f0;
        }
        @page {
            size: A4;
            margin: 0;
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Outfit', sans-serif;
            background-color: #f8fafc;
            color: var(--text-dark);
            -webkit-print-color-adjust: exact;
        }
        .invoice-wrapper {
            width: 210mm;
            min-height: auto;
            margin: 0 auto;
            background: white;
            padding: 20mm;
            position: relative;
            z-index: 1;
        }
        .page-watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 80%;
            opacity: 0.06;
            z-index: -1;
            pointer-events: none;
        }
        .header {
            background-color: rgba(0, 0, 0, 0.08);
            border-bottom: 1.5px solid var(--border);
            margin: -20mm -20mm 40px -20mm;
            padding: 40px 20mm;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        .invoice-title-container {
            text-align: right;
        }
        .logo-container img {
            max-height: 120px;
            object-fit: contain;
        }
        .invoice-title h1 {
            font-size: 34px;
            color: var(--primary);
            text-transform: uppercase;
            letter-spacing: 2.5px;
            margin-bottom: 5px;
            font-weight: 800;
        }
        .invoice-title p {
            font-size: 16px;
            color: var(--secondary);
            font-weight: 500;
        }
        .info-grid {
            background-color: rgba(0, 0, 0, 0.007);
            border: 1.2px solid var(--border);
            border-radius: 12px;
            display: flex;
            margin-bottom: 30px;
            position: relative;
        }
        .info-grid .info-section {
            flex: 1;
            padding: 30px;
            position: relative;
        }
        .info-grid .info-section:first-child::after {
            content: '';
            position: absolute;
            right: 0;
            top: 20%;
            height: 60%;
            width: 1.5px;
            background-color: var(--border);
        }

        .info-section h3 {
            font-size: 13px;
            text-transform: uppercase;
            color: var(--secondary);
            letter-spacing: 1px;
            margin-bottom: 20px;
            padding-left: 0;
            font-weight: 600;
        }
        .info-content .detail {
            font-size: 16px;
            color: var(--text-light);
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 12px;
        }
        .info-content .label {
            color: var(--text-dark);
            font-weight: 600;
            min-width: 120px;
        }
        .info-content .value-name {
            font-size: 19px;
            font-weight: 700;
            color: var(--text-dark);
            margin-bottom: 6px;
        }
        .status-badge {
            display: inline-block;
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.5px;
            background-color: ${statusBg};
            color: ${statusColor};
            border: 1px solid ${statusColor}40;
        }
        .stay-details-card {
            background-color: rgba(0, 0, 0, 0.007);
            border: 1.2px solid var(--border);
            border-radius: 12px;
            margin-bottom: 30px;
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            position: relative;
        }
        .stay-item {
            padding: 25px 25px 12px 25px;
            position: relative;
        }
        .stay-item:not(:last-child)::after {
            content: '';
            position: absolute;
            right: 0;
            top: 20%;
            height: 60%;
            width: 1.5px;
            background-color: var(--border);
        }
        .stay-item h4 {
            font-size: 11px;
            color: var(--primary);
            text-transform: uppercase;
            margin-bottom: 10px;
        }
        .stay-item p {
            font-size: 16px;
            font-weight: 600;
            color: var(--text-dark);
        }
        .invoice-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
        }
        .invoice-table th {
            background: rgba(0, 0, 0, 0.099);
            text-align: left;
            padding: 14px 15px;
            font-size: 14px;
            text-transform: uppercase;
            color: var(--secondary);
            border-bottom: 2px solid var(--border);
        }
        .invoice-table td {
            padding: 20px 15px;
            border-bottom: 1px solid var(--border);
            font-size: 16px;
        }
        .summary-wrapper {
            display: flex;
            justify-content: flex-end;
        }
        .summary-wrapper.partial-layout {
            margin-bottom: 8px;
        }
        .summary-box {
            width: 350px;
            background-color: rgba(0, 0, 0, 0.007);
            border: 1px solid var(--border);
            padding: 15px;
            border-radius: 12px;
        }
        .summary-box.compact {
            padding: 12px;
        }
        .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 16px;
        }
        .summary-row.compact {
            padding: 6px 0;
            font-size: 15px;
        }
        .summary-row.total {
            margin-top: 8px;
            padding-top: 12px;
            border-top: 1.5px solid var(--primary);
            font-weight: 700;
            font-size: 18px;
            color: var(--primary);
        }
        .summary-row.paid { color: #16a34a; }
        .summary-row.due { color: #dc2626; font-weight: 600; }
        .footer {
            margin-top: 60px;
            padding-top: 30px;
            border-top: 1px solid var(--border);
            color: var(--secondary);
            font-size: 12px;
            text-align: center;
        }
        .footer strong { color: var(--primary); }
        
        .rules-page {
            width: 210mm;
            margin: 0 auto;
            background: white;
            padding: 20mm;
            page-break-before: always;
            break-before: page;
            font-family: 'Outfit', sans-serif;
        }
        .rules-header {
            background-color: #1f2f26;
            color: white;
            padding: 18px 24px;
            border-radius: 10px;
            margin-bottom: 24px;
            text-align: center;
        }
        .rules-header h2 {
            font-size: 20px;
            font-weight: 700;
            letter-spacing: 1px;
            margin-bottom: 4px;
        }
        .rules-header p {
            font-size: 13px;
            opacity: 0.8;
        }
        .rules-list {
            list-style: none;
            padding: 0;
            margin: 0 0 20px 0;
        }
        .rules-list li {
            display: flex;
            gap: 12px;
            align-items: flex-start;
            padding: 10px 14px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 14px;
            line-height: 1.65;
            color: #1e293b;
        }
        .rules-list li:last-child { border-bottom: none; }
        .rules-list li:nth-child(even) { background-color: #f8fafc; border-radius: 6px; }
        .rule-num {
            min-width: 26px;
            height: 26px;
            border-radius: 50%;
            background-color: #1f2f26;
            color: white;
            font-size: 12px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            margin-top: 2px;
        }
        .rules-note {
            background-color: #fff9e6;
            border: 1.5px solid #fde68a;
            border-radius: 10px;
            padding: 14px 18px;
            font-size: 13.5px;
            line-height: 1.7;
            color: #92400e;
        }
        .rules-note strong { color: #78350f; }
        @media print {
            body { background: white; }
            .invoice-wrapper {
                box-shadow: none;
                min-height: auto;
                height: auto;
            }
            .rules-page {
                box-shadow: none;
            }
        }
    </style>
</head>
<body>
    <div class="invoice-wrapper">
        <img src="${darkLogo}" class="page-watermark" alt="Watermark" onerror="this.style.display='none'">
        <div class="header">
            <div class="logo-container">
                <img src="${logoUrl}" alt="Logo" onerror="this.style.display='none'">
            </div>
            <div class="invoice-title-container">
                <div class="invoice-title">
                    <h1>Invoice</h1>
                    <p># ${invoiceNumber}</p>
                    <div style="margin-top: 8px;">
                        <span class="status-badge">${status}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="info-grid">
            <div class="info-section">
                <h3>Billed To</h3>
                <div class="info-content">
                    <p class="detail"><span class="label">Name:</span> <span>${booking.user?.name || 'Customer'}</span></p>
                    <p class="detail"><span class="label">Mobile no.:</span> <span>${booking.user?.mobileNo || booking.user?.mobile || 'N/A'}</span></p>
                    <p class="detail"><span class="label">Email:</span> <span>${booking.user?.email || '-'}</span></p>
                </div>
            </div>
            <div class="info-section">
                <h3>Invoice Information</h3>
                <div class="info-content">
                    <p class="detail"><span class="label">Booking Date:</span> <span>${invoiceDate}</span></p>

                    <p class="detail"><span class="label">Property:</span> <span>${booking.farmhouse?.name || 'Farmhouse'}</span></p>
                </div>
            </div>
        </div>

        <div class="stay-details-card">
            <div class="stay-item">
                <h4>Check In</h4>
                <p style="margin: 0;">${checkIn}</p>
                <p style="font-size: 12px; font-weight: 400; opacity: 0.8; margin: 8px 0 0 0;">${checkInTime}</p>
            </div>
            <div class="stay-item">
                <h4>Check Out</h4>
                <p style="margin: 0;">${checkOut}</p>
                <p style="font-size: 12px; font-weight: 400; opacity: 0.8; margin: 8px 0 0 0;">${checkOutTime}</p>
            </div>
            <div class="stay-item">
                <h4>Members</h4>
                <p>${guests} Persons</p>
                ${(extraPersonCount > 0) ? `
                    <p style="font-size: 11px; color: var(--secondary); margin-top: 4px;">
                        + ${extraPersonCount} Extra @ ${formatCurrency(extraPersonRate)}
                    </p>` : ''}
            </div>
            <div class="stay-item">
                <h4>Duration</h4>
                <p>${durationHours} Hours</p>
            </div>
        </div>

        <table class="invoice-table">
            <thead>
                <tr>
                    <th>Description</th>
                    <th style="text-align: right;">Amount</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        <div style="font-weight: 600;">${booking.farmhouse?.name || 'Farmhouse Booking'}</div>
                        <div style="font-size: 12px; color: var(--secondary); margin-top: 4px;">Booking for ${durationHours} Hours - ${bookingType}</div>
                    </td>
                    <td style="text-align: right;">${formatCurrency(basicRate)}</td>
                </tr>
            </tbody>
        </table>

        <div class="summary-wrapper ${useCompactLayout ? 'partial-layout' : ''}">
            <div class="summary-box ${useCompactLayout ? 'compact' : ''}">
                <div class="summary-row ${useCompactLayout ? 'compact' : ''}">
                    <span>Subtotal (Basic Rate)</span>
                    <span>${formatCurrency(basicRate)}</span>
                </div>
                ${extraPersonTotal > 0 ? `
                <div class="summary-row ${useCompactLayout ? 'compact' : ''}">
                    <span>Extra Person Rent (${extraPersonCount} persons)</span>
                    <span>${formatCurrency(extraPersonTotal)}</span>
                </div>
                ` : ''}
                ${discount > 0 ? `
                <div class="summary-row ${useCompactLayout ? 'compact' : ''}" style="color: #16a34a;">
                    <span>Discount</span>
                    <span>-${formatCurrency(discount)}</span>
                </div>
                ` : ''}
                <div class="summary-row total ${useCompactLayout ? 'compact' : ''}">
                    <span>Final Total</span>
                    <span>${formatCurrency(finalTotal)}</span>
                </div>
                
                ${(status === 'CANCEL' || status === 'CANCELLED') ? `
                <div class="summary-row" style="font-weight: 700; background-color: #fef2f2; color: #dc2626; padding: 10px; border-radius: 8px; margin-top: 10px; border: 1px solid #fecaca;">
                    <span>Order Status</span>
                    <span>CANCELLED ORDER</span>
                </div>
                ` : (String(booking.paymentStatus || '').toLowerCase() === 'paid') ? `
                <div class="summary-row paid" style="font-weight: 700; background-color: #e6f7ed; padding: 10px; border-radius: 8px; margin-top: 10px;">
                    <span>Payment Status</span>
                    <span>FULLY PAID</span>
                </div>
                ` : (status === 'INCOMPLETE' || status === 'PENDING') ? `
                <div class="summary-row paid ${useCompactLayout ? 'compact' : ''}">
                    <span>Paid Amount</span>
                    <span>${formatCurrency(normalizedPaidAmount)}</span>
                </div>
                <div class="summary-row due ${useCompactLayout ? 'compact' : ''}">
                    <span>Balance Due</span>
                    <span>${formatCurrency(finalTotal)}</span>
                </div>
                ` : `
                <div class="summary-row paid ${useCompactLayout ? 'compact' : ''}">
                    <span>Paid Amount</span>
                    <span>${formatCurrency(normalizedPaidAmount)}</span>
                </div>
                <div class="summary-row due ${useCompactLayout ? 'compact' : ''}">
                    <span>Balance Due</span>
                    <span>${formatCurrency(normalizedRemainingAmount)}</span>
                </div>
                `}
            </div>
        </div>

        <div style="margin-top: ${useCompactLayout ? '18px' : '50px'}; padding: ${useCompactLayout ? '12px' : '15px'}; border-radius: 12px; background-color: rgba(31, 47, 38, 0.04); border: 1px solid var(--border);">
            <p style="font-size: ${useCompactLayout ? '13px' : '14px'}; color: var(--text-dark); margin: 0; text-align: center; line-height: ${useCompactLayout ? '1.45' : '1.6'};">
                <strong style="color: var(--primary);">Note:</strong> “A refundable security deposit of 2000 will be collected from you and returned within 2 hours after checkout.”
            </p>
        </div>
    </div>

    <!-- ====== PAGE 2: GUJARATI RULES ====== -->
    <div class="rules-page">
        <div class="rules-header">
            <h2>ફાર્મ હાઉસ નિયમો</h2>
            <p>આ નિયમોનું પાલન કરવું ફરજિયાત રહેશે</p>
        </div>

        <ul class="rules-list">
            <li>
                <span class="rule-num">1</span>
                <span>ફેમીલી માટે જ ફાર્મનો ઉપયોગ કરી શકાશે. વર્ષ <strong>18+ વાળા</strong> ને જ ફાર્મ માં એન્ટ્રી મળશે.</span>
            </li>
            <li>
                <span class="rule-num">2</span>
                <span>સ્મોકિંગ, દારૂ કે નોનવેજ લઈ જવાની સખત મનાઈ છે. કોઈપણ જગ્યાએ પાન માવા ખાઈને થુંકવું નહિ.</span>
            </li>
            <li>
                <span class="rule-num">3</span>
                <span><strong>સફાઈ ચાર્જ ₹૩૦૦ ત્યાં અલગ થી આપવાના રહેશે.</strong></span>
            </li>
            <li>
                <span class="rule-num">4</span>
                <span>કોઈપણ વસ્તુની ભાંગટુટ થાય તો તે જવાબદારી ભાડે લેનારની રહેશે.</span>
            </li>
            <li>
                <span class="rule-num">5</span>
                <span>ચેક ઈન સમયે <strong>₹૨૦૦૦ સિક્યુરિટી ડિપોઝિટ</strong> આપવાની રહેશે જે ચેકઆઉટ સમયે પરત મળશે.</span>
            </li>
            <li>
                <span class="rule-num">6</span>
                <span>કોઈપણ જાતની ગેરકાનૂની પ્રવૃત્તિ ચલાવી લેવામાં આવશે નહીં. છતા પણ અમારી જાણ બહાર કોઈ ગેરકાનૂની પ્રવૃત્તિ થશે તો તેની સંપૂર્ણ જવાબદારી ભાડે લેનારની રહેશે.</span>
            </li>
            <li>
                <span class="rule-num">7</span>
                <span>ફાર્મહાઉસની અંદર કે બહાર કોઈ પણ જાતની દૂઘૅટના થાય તો તેની જવાબદારી ફાર્મ ભાડે રાખનારની રહેશે. (નાના બાળકોની જવાબદારી તમારી રહેશે.)</span>
            </li>
            <li>
                <span class="rule-num">8</span>
                <span>ભીનાં કપડાં પહેરીને બંગલામાં પ્રવેશ કરવું નહી. જો ભૂલથી પ્રવેશ થાય તો સાફ-સફાઈ તુરંત તમારે જાતે કરવાની રહેશે.</span>
            </li>
            <li>
                <span class="rule-num">9</span>
                <span>ફાર્મનું બુકીંગ કરાવતી વખતે <strong>૧૦૦% એડવાન્સ</strong> આપવાનું રહેશે અને કોઈ એક વ્યક્તિ ની <strong>આધાર કાર્ડ</strong> ની નકલ આપવાની રહેશે.</span>
            </li>
            <li>
                <span class="rule-num">10</span>
                <span>સંજોગો વસાત પાવર જાય કે પાવરનો કાપ આવે તો તેની જવાબદારી ફાર્મ માલિકની રહેશે નહિ.</span>
            </li>
            <li>
                <span class="rule-num">11</span>
                <span>બુકીંગ કરાવેલ તારીખ કેન્સલ થાય તો બીજી કોઈ તારીખ માં ટ્રાન્સફર થશે નહીં અને બુકીંગ ના પૈસા પરત મળશે નહી. ખાસ નોંધ લેવી.</span>
            </li>
            <li>
                <span class="rule-num">12</span>
                <span>બાથ (Pool/Tank) માં કચરો નાખવો નહી. બાથ માં સાબુ-શેમ્પૂ થી નાહવું નહી. ફાર્મ ની વસ્તુ જેમ હોઈ તેમ મૂકી ને જવાનું રહેશે.</span>
            </li>
            <li>
                <span class="rule-num">13</span>
                <span>લોન ઉપર ચૂલો / તાપણું કરવું નહી. ફૂલ-ઝાડ ને નુકસાન કરવું નહી.</span>
            </li>
            <li>
                <span class="rule-num">14</span>
                <span>જતી વખતે કચરા નું ઝભ્ભો ભરી બહાર સાઈડ માં મૂકી દેવું. D.J. / સાઉન્ડ સાંજે <strong>10:00 વાગ્યા</strong> પછી વગાડ​વું નહી.</span>
            </li>
            <li>
                <span class="rule-num">15</span>
                <span>છેલ્લો નિર્ણય ફાર્મ હાઉસ માલિકનો રહેશે.</span>
            </li>
            <li>
                <span class="rule-num">16</span>
                <span>ફાર્મ પર કોઈ પણ કિંમતી ચીજ-વસ્તુ ખોવાઈ જાય તો તેની જવાબદારી ફાર્મ ભાડે રાખનારની રહેશે, ફાર્મ ના માલિક ની નહી.</span>
            </li>
        </ul>

        <div class="rules-note">
            <strong>&#9888; નોંધ:</strong> ફાર્મ પર કોઈ આલ્કોહોલ કે બીજી કોઈ પાર્ટી કરવી નહિ. જો કોઈ ફાર્મ પર આ નિયમ તોડ​તાં પકડાશે તો <strong>પોલીસ ને જાણ</strong> કરી દેવામાં આવશે, ફાર્મ <strong>તરત ખાલી</strong> કરવાનું રહેશે અને <strong>ભાડું પરત (refund) મળશે નહીં</strong>. સંપૂર્ણ જવાબદારી ભાડે રાખ​નારની રહેશે.
        </div>

        <div class="rules-note" style="background-color: #fef2f2; border: 1.5px solid #fca5a5; color: #991b1b; margin-top: 12px;">
            <strong>&#9888; સરકારી ચેતવણી:</strong> અમારી કંપની ગવર્મેન્ટ રજિસ્ટર્ડ (Government Registered) છે. જો કોઈપણ નિયમોનો ભંગ થશે, તો સરકારી કાયદા મુજબ કડક કાનૂની અને પોલીસ કાર્યવાહી કરવામાં આવશે, જેની સંપૂર્ણ જવાબદારી માત્ર અને માત્ર ભાડે રાખનારની રહેશે.
        </div>

        <div style="margin-top: 30px; text-align: center; font-size: 12px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 16px;">
            આ નિયમો વાંચ્યા અને સ્વીકાર્યા &nbsp;|&nbsp; Farmstay Hub &copy; ${new Date().getFullYear()}
        </div>
    </div>

</body>
</html>`;
};
